import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Search, Plus, Pencil, Trash2, Clock, User, Loader2, RefreshCw, X, ImageIcon, Star } from "lucide-react";
import { blogService, type BlogPost } from "../../../lib/services/blogService";
import type { Models } from "appwrite";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";

type BlogDoc = BlogPost & Models.Document;

const CATEGORY_COLORS: Record<string, string> = {
    HVAC: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800",
    Refrigeration: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800",
    "Heat Pump": "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
    Electrical: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
    Engineering: "bg-primary/10 text-primary border-primary/20",
    "Case Study": "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
};

export function AdminBlog() {
    const navigate = useNavigate();
    const [posts, setPosts] = useState<BlogDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        blogService.list()
            .then(setPosts)
            .catch(console.error)
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const handleDelete = async (id: string) => {
        if (!confirm("Delete this post? This cannot be undone.")) return;
        setDeletingId(id);
        setPosts((p) => p.filter((x) => x.$id !== id));
        try { await blogService.remove(id); }
        catch { load(); }
        finally { setDeletingId(null); }
    };

    const filtered = posts.filter(
        (p) =>
            p.title.toLowerCase().includes(search.toLowerCase()) ||
            p.category.toLowerCase().includes(search.toLowerCase()) ||
            p.author.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="w-full min-w-0 space-y-4">
            {/* ── Header row ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
                <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="flex items-center gap-3 shrink-0">
                        <h2 className="text-base font-semibold text-foreground">Blog Posts</h2>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{posts.length}</span>
                    </div>
                    {/* Mobile actions */}
                    <div className="sm:hidden flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={load} className="h-8 w-8 p-0 text-muted-foreground hover:text-white">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button onClick={() => navigate("/admin/blog/new")} size="sm" className="h-8 gap-1.5 px-3">
                            <Plus className="w-3.5 h-3.5" /> <span className="hidden xs:inline">New</span>
                        </Button>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Search */}
                    <div className="relative flex-1 sm:w-[260px] max-w-full">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search posts…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-8 text-sm bg-background shadow-none focus-visible:ring-1 border border-border w-full"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Desktop actions */}
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={load} className="h-8 text-muted-foreground hover:text-white gap-1.5">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </Button>
                        <Button onClick={() => navigate("/admin/blog/new")} size="sm" className="h-8 gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> New Post
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 bg-card rounded border border-border min-h-[40vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-sm font-medium text-muted-foreground">Loading posts...</p>
                </div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-4">
                        {filtered.map((post) => (
                            <Card key={post.$id} className="overflow-hidden shadow-none">
                                {post.imageUrl && (
                                    <div className="h-32 overflow-hidden border-b border-border">
                                        <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <p className="text-sm font-semibold text-foreground leading-snug flex-1">{post.title}</p>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <Badge variant="outline" className={`border-transparent font-normal ${CATEGORY_COLORS[post.category] ?? "bg-muted text-muted-foreground"}`}>
                                                {post.category}
                                            </Badge>
                                            {post.featured === "true" && (
                                                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-sm">
                                                    <Star className="w-2.5 h-2.5 fill-amber-500" /> Featured
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-4">
                                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                                        <span>{post.date}</span>
                                    </div>
                                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                                        <Badge variant="outline" className={post.status === "Published" ? "bg-emerald-50 text-emerald-700 border-transparent" : "bg-muted text-muted-foreground border-transparent"}>
                                            {post.status}
                                        </Badge>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="icon" className="h-8 w-8 shadow-none" onClick={() => navigate(`/admin/blog/${post.$id}`)}>
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button variant="outline" size="icon" className="h-8 w-8 shadow-none text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete(post.$id)} disabled={deletingId === post.$id}>
                                                {deletingId === post.$id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                        {filtered.length === 0 && (
                            <div className="text-center py-16 bg-card rounded border border-border">
                                <p className="text-sm text-muted-foreground mb-3">No posts found.</p>
                                <Button variant="link" onClick={() => navigate("/admin/blog/new")} className="text-primary">Write your first post →</Button>
                            </div>
                        )}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block border border-border rounded overflow-hidden shadow-none">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                    <TableHead className="w-[300px] font-semibold">Title</TableHead>
                                    <TableHead className="w-[150px] font-semibold">Category</TableHead>
                                    <TableHead className="w-[150px] font-semibold">Author</TableHead>
                                    <TableHead className="w-[120px] font-semibold">Read Time</TableHead>
                                    <TableHead className="w-[120px] font-semibold">Date</TableHead>
                                    <TableHead className="w-[120px] font-semibold">Status</TableHead>
                                    <TableHead className="w-[100px] text-right font-semibold">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((post) => (
                                    <TableRow key={post.$id} className="group cursor-pointer" onClick={() => navigate(`/admin/blog/${post.$id}`)}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {post.imageUrl ? (
                                                    <img src={post.imageUrl} alt={post.title} className="w-10 h-10 rounded object-cover border border-border shrink-0" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center border border-border shrink-0">
                                                        <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-foreground line-clamp-1 text-sm">{post.title}</p>
                                                    {post.featured === "true" && (
                                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-sm mt-0.5">
                                                            <Star className="w-2.5 h-2.5 fill-amber-500" /> Featured
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`border-transparent font-normal ${CATEGORY_COLORS[post.category] ?? "bg-muted text-muted-foreground"}`}>
                                                {post.category}
                                            </Badge>
                                        </TableCell>
                                        <TableCell><span className="text-sm text-muted-foreground">{post.author}</span></TableCell>
                                        <TableCell><span className="text-sm text-muted-foreground">{post.readTime}</span></TableCell>
                                        <TableCell><span className="text-sm text-muted-foreground whitespace-nowrap">{post.date}</span></TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`font-normal ${post.status === "Published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground border-border"}`}>
                                                {post.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/blog/${post.$id}`)} className="h-8 w-8 text-muted-foreground hover:text-white">
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(post.$id)} disabled={deletingId === post.$id} className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                                                    {deletingId === post.$id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-32 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <p className="text-sm text-muted-foreground">No posts found.</p>
                                                <Button variant="link" onClick={() => navigate("/admin/blog/new")} className="text-primary">Write your first post →</Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </>
            )}
        </div>
    );
}
